import { useMemo } from 'react';

import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_FIELD,
  CHART_METRIC_VIEW_TYPE,
  CHART_TOOLTIP_TARGET_TYPE,
} from '@/entities/chart/model/constants';
import useChartColor from '@/features/(charts)/chart-renderer/hooks/use-chart-color';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { getPercentStringFormat } from '@/utils/util';

const EMPTY_FIELD_ITEM_META: ChartFieldItemMeta[] = [];

type Props = {
  chart: ChartBase;
  tableData: ChartCrossTableRes;
};

const useChartCrossAxisData = (props: Props) => {
  const { chart, tableData } = props;
  const { field } = chart;
  // For CROSS_AXIS, use the first series' cross_field
  const cross_field = chart.series[0]?.cross_field;
  if (!cross_field) throw new Error('Cross field is not defined');
  const fieldItemMeta = tableData.field_item_meta ?? EMPTY_FIELD_ITEM_META;
  const getLabel = useChartLabel(fieldItemMeta);
  const getColor = useChartColor(cross_field);

  const crossTableData = useMemo(() => {
    return chartUtils.getSortedRowItems(chart, tableData, []) as ChartCrossTableRes;
  }, [chart, tableData]);

  const sortedCandidates = useMemo(() => {
    const primaryTable = crossTableData.tables[0] || [];
    const origin = primaryTable.map((x) => x.x);
    for (const candidate of chart.field.candidates || []) {
      if (!origin.includes(candidate)) {
        origin.push(candidate);
      }
    }
    return origin;
  }, [chart.field.candidates, crossTableData.tables]);

  const crossAxisData = useMemo(() => {
    const datasets = [];
    const isPercentage =
      chart.series[0]?.options?.view_type === CHART_METRIC_VIEW_TYPE.PERCENTAGE;
    const reversedTableData = chartUtils.transformToYBased(
      crossTableData.tables[0] || [],
    );

    const xLabels: string[] =
      sortedCandidates.map((x) => getLabel(field.type, x)) || [];

    const xTotalMap = chartUtils.getKeyValueMapByKeys(sortedCandidates);
    reversedTableData.forEach((y) => {
      y.values.forEach((x) => {
        xTotalMap[x.x] += x.value;
      });
    });

    // FEEDBACK_ALL 타입인 경우 고정값 'all'을 사용, 아니면 candidates 사용
    const yAxisCandidates =
      cross_field.type === CHART_FIELD.FEEDBACK_ALL
        ? [CHART_ALL_FEEDBACK_ALIAS]
        : cross_field.candidates || [];

    if (chart.target.is_compare) {
      if (!crossTableData.compare_tables?.[0])
        throw new Error('Compare table data is not defined');
      const reversedCompareTableData = chartUtils.transformToYBased(
        crossTableData.compare_tables[0] || [],
      );

      const compareXTotalMap =
        chartUtils.getKeyValueMapByKeys(sortedCandidates);

      reversedCompareTableData.forEach((y) => {
        y.values.forEach((x) => {
          compareXTotalMap[x.x] += x.value;
        });
      });

      const compareDatasets = yAxisCandidates.map((y) => {
        const yData = reversedCompareTableData.find((d) => d.y === y);
        const xMap = chartUtils.getKeyValueMapByKeys(sortedCandidates);
        yData?.values.forEach((x) => {
          xMap[x.x] = isPercentage
            ? Number(getPercentStringFormat(compareXTotalMap[x.x], x.value, 2))
            : x.value;
        });
        return {
          label: getLabel(cross_field.type, y),
          data: sortedCandidates.map((x) => xMap[x]),
          backgroundColor: getAlphaColor(getColor(y), 0.6),
          stack: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
          date_type: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
          meta: {
            x: sortedCandidates || [],
            y: y,
          },
        };
      });

      datasets.push(...compareDatasets);
    }

    const currentDatasets = yAxisCandidates.map((y) => {
      const yData =
        cross_field.type === CHART_FIELD.FEEDBACK_ALL
          ? reversedTableData[0]
          : reversedTableData.find((d) => d.y === y);
      const xMap = chartUtils.getKeyValueMapByKeys(sortedCandidates);
      yData?.values.forEach((x) => {
        xMap[x.x] = isPercentage
          ? Number(getPercentStringFormat(xTotalMap[x.x], x.value, 2))
          : x.value;
      });

      return {
        label: getLabel(cross_field.type, y),
        data: sortedCandidates.map((x) => xMap[x]),
        backgroundColor: getColor(y),
        stack: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
        date_type: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
        meta: {
          x: sortedCandidates || [],
          y: y,
        },
      };
    });

    datasets.push(...currentDatasets);

    return {
      labels: xLabels,
      datasets: datasets,
    };
  }, [
    chart.series,
    chart.target.is_compare,
    crossTableData.compare_tables,
    crossTableData.tables,
    cross_field.candidates,
    cross_field.type,
    field.type,
    getColor,
    getLabel,
    sortedCandidates,
  ]);

  return crossAxisData;
};

export default useChartCrossAxisData;
